/** REST-вызовы серверов, каналов, ролей, участников и приглашений (docs/api/openapi.yaml). */
import { api } from './api'
import { authed } from './auth'
import type { AuditLog, Ban, Channel, Emoji, Guild, GuildPartial, ImportJob, ImportOptions, Invite, Member, Message, Overwrite, Role, Sticker, ThreadMember, UserGuildSettings, VoiceJoin, VoiceState, AutomodRule, AutomodRuleInput, Webhook } from '@/types/api'

export type GuildTemplate = 'default' | 'gaming' | 'study' | 'community' | 'friends' | 'empty'

/** Сервер Discord в списке владельца при переносе. */
export interface DiscordGuild {
  id: string
  name: string
  icon: string
  owner: boolean
  can_import: boolean
  bot_present: boolean
}

export interface DiscordImportStatus {
  enabled: boolean
  authorize_url?: string
  linked?: boolean
  guilds?: DiscordGuild[]
}

export interface ChannelPatch {
  name?: string
  topic?: string | null
  position?: number
  parent_id?: string | null
  nsfw?: boolean
  rate_limit_per_user?: number
  bitrate?: number
  user_limit?: number
  permission_overwrites?: Overwrite[]
}

export interface SettingsPatch {
  message_notifications?: number
  muted?: boolean
  mute_config?: { end_time: string | null } | null
  suppress_everyone?: boolean
  suppress_roles?: boolean
  channel_overrides?: Record<string, { message_notifications?: number; muted?: boolean; mute_config?: { end_time: string | null } | null; collapsed?: boolean }>
}

export interface SearchParams {
  content?: string
  channel_id?: string
  author_id?: string
  mentions?: string
  has?: 'link'
  offset?: number
}

export interface ThreadPatch {
  name?: string
  archived?: boolean
  locked?: boolean
  auto_archive_duration?: number
  rate_limit_per_user?: number
}

export interface ThreadList {
  threads: Channel[]
  members: ThreadMember[]
  has_more: boolean
}

export interface RolePatch {
  name?: string
  color?: number
  hoist?: boolean
  permissions?: string
  mentionable?: boolean
}

export const guildApi = {
  mine: () => authed<GuildPartial[]>('/users/@me/guilds'),
  create: (input: { name: string; template?: GuildTemplate }) => authed<Guild>('/guilds', { method: 'POST', body: JSON.stringify(input) }),
  get: (guildId: string, withCounts = false) => authed<Guild>(`/guilds/${guildId}${withCounts ? '?with_counts=true' : ''}`),
  update: (guildId: string, patch: Partial<Pick<Guild, 'name' | 'icon' | 'banner' | 'description' | 'system_channel_id' | 'rules_channel_id' | 'owner_id' | 'afk_channel_id' | 'afk_timeout' | 'default_message_notifications' | 'verification_level' | 'tag' | 'tag_color' | 'premium_progress_bar_enabled'>>) =>
    authed<Guild>(`/guilds/${guildId}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  remove: (guildId: string) => authed<void>(`/guilds/${guildId}`, { method: 'DELETE' }),
  leave: (guildId: string) => authed<void>(`/users/@me/guilds/${guildId}`, { method: 'DELETE' }),

  channels: (guildId: string) => authed<Channel[]>(`/guilds/${guildId}/channels`),
  createChannel: (guildId: string, input: { name: string; type: number; parent_id?: string | null; topic?: string }) =>
    authed<Channel>(`/guilds/${guildId}/channels`, { method: 'POST', body: JSON.stringify(input) }),
  updateChannel: (channelId: string, patch: ChannelPatch) => authed<Channel>(`/channels/${channelId}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  deleteChannel: (channelId: string) => authed<Channel>(`/channels/${channelId}`, { method: 'DELETE' }),
  reorderChannels: (guildId: string, items: { id: string; position?: number; parent_id?: string | null }[]) =>
    authed<void>(`/guilds/${guildId}/channels`, { method: 'PATCH', body: JSON.stringify(items) }),
  createThread: (channelId: string, messageId: string | null, input: { name: string; auto_archive_duration?: number }) =>
    authed<Channel>(messageId ? `/channels/${channelId}/messages/${messageId}/threads` : `/channels/${channelId}/threads`, { method: 'POST', body: JSON.stringify(input) }),
  updateThread: (threadId: string, patch: ThreadPatch) => authed<Channel>(`/channels/${threadId}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  joinThread: (threadId: string) => authed<void>(`/channels/${threadId}/thread-members/@me`, { method: 'PUT' }),
  leaveThread: (threadId: string) => authed<void>(`/channels/${threadId}/thread-members/@me`, { method: 'DELETE' }),
  threadMembers: (threadId: string) => authed<ThreadMember[]>(`/channels/${threadId}/thread-members`),
  activeThreads: (guildId: string) => authed<ThreadList>(`/guilds/${guildId}/threads/active`),
  archivedThreads: (channelId: string, before?: string) =>
    authed<ThreadList>(`/channels/${channelId}/threads/archived/public?limit=50${before ? `&before=${encodeURIComponent(before)}` : ''}`),
  putOverwrite: (channelId: string, targetId: string, ow: { type: 0 | 1; allow: string; deny: string }) =>
    authed<void>(`/channels/${channelId}/permissions/${targetId}`, { method: 'PUT', body: JSON.stringify(ow) }),
  deleteOverwrite: (channelId: string, targetId: string) => authed<void>(`/channels/${channelId}/permissions/${targetId}`, { method: 'DELETE' }),

  roles: (guildId: string) => authed<Role[]>(`/guilds/${guildId}/roles`),
  createRole: (guildId: string, input: RolePatch) => authed<Role>(`/guilds/${guildId}/roles`, { method: 'POST', body: JSON.stringify(input) }),
  updateRole: (guildId: string, roleId: string, patch: RolePatch) => authed<Role>(`/guilds/${guildId}/roles/${roleId}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  deleteRole: (guildId: string, roleId: string) => authed<void>(`/guilds/${guildId}/roles/${roleId}`, { method: 'DELETE' }),
  reorderRoles: (guildId: string, items: { id: string; position: number }[]) =>
    authed<Role[]>(`/guilds/${guildId}/roles`, { method: 'PATCH', body: JSON.stringify(items) }),

  members: (guildId: string, limit = 1000, after?: string) =>
    authed<Member[]>(`/guilds/${guildId}/members?limit=${limit}${after ? `&after=${after}` : ''}`),
  searchMembers: (guildId: string, query: string, limit = 20) =>
    authed<Member[]>(`/guilds/${guildId}/members/search?query=${encodeURIComponent(query)}&limit=${limit}`),
  member: (guildId: string, userId: string) => authed<Member>(`/guilds/${guildId}/members/${userId}`),
  updateSelf: (guildId: string, nick: string | null) => authed<Member>(`/guilds/${guildId}/members/@me`, { method: 'PATCH', body: JSON.stringify({ nick }) }),
  /** Аватар под сервер («Кличат Плюс»): data URI — поставить, null — вернуть профильный. */
  setMemberAvatar: (guildId: string, avatar: string | null) =>
    authed<Member>(`/guilds/${guildId}/members/@me`, { method: 'PATCH', body: JSON.stringify({ avatar }) }),
  updateMember: (guildId: string, userId: string, patch: { nick?: string | null; roles?: string[]; communication_disabled_until?: string | null; mute?: boolean; deaf?: boolean; channel_id?: string | null }) =>
    authed<Member>(`/guilds/${guildId}/members/${userId}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  kick: (guildId: string, userId: string) => authed<void>(`/guilds/${guildId}/members/${userId}`, { method: 'DELETE' }),
  addRole: (guildId: string, userId: string, roleId: string) => authed<void>(`/guilds/${guildId}/members/${userId}/roles/${roleId}`, { method: 'PUT' }),
  removeRole: (guildId: string, userId: string, roleId: string) => authed<void>(`/guilds/${guildId}/members/${userId}/roles/${roleId}`, { method: 'DELETE' }),

  /** Эмодзи сервера: image — data URI до 256 КБ (MANAGE_GUILD_EXPRESSIONS). */
  // импорт из Discord
  imports: (guildId: string) => authed<ImportJob[]>(`/guilds/${guildId}/imports`),
  /** Перенос прямо из Discord: состояние входа и список серверов владельца. */
  discordImportStatus: (guildId: string) => authed<DiscordImportStatus>(`/guilds/${guildId}/imports/discord`),
  discordLink: (code: string, state: string) => authed<{ guild_id: string }>('/discord/link', { method: 'POST', body: JSON.stringify({ code, state }) }),
  discordInvite: (sourceGuildId: string) => authed<{ url: string }>(`/discord/invite?guild_id=${sourceGuildId}`),
  discordImport: (guildId: string, sourceGuildId: string, claims: boolean) =>
    authed<{ job: ImportJob; codes: { username: string; display_name: string; code: string }[] }>(`/guilds/${guildId}/imports/discord`, {
      method: 'POST',
      body: JSON.stringify({ source_guild_id: sourceGuildId, claims }),
    }),
  importJob: (guildId: string, jobId: string) => authed<ImportJob>(`/guilds/${guildId}/imports/${jobId}`),
  startImport: (guildId: string, jobId: string, options: ImportOptions) => authed<ImportJob>(`/guilds/${guildId}/imports/${jobId}/start`, { method: 'POST', body: JSON.stringify(options) }),
  cancelImport: (guildId: string, jobId: string) => authed<void>(`/guilds/${guildId}/imports/${jobId}`, { method: 'DELETE' }),
  claimImport: (guildId: string, code: string) => authed<{ roles: Role[] }>(`/guilds/${guildId}/import-claims`, { method: 'POST', body: JSON.stringify({ code }) }),
  stickers: (guildId: string) => authed<Sticker[]>(`/guilds/${guildId}/stickers`),
  createSticker: (guildId: string, body: { name: string; description?: string; tags?: string; file: string }) => authed<Sticker>(`/guilds/${guildId}/stickers`, { method: 'POST', body: JSON.stringify(body) }),
  updateSticker: (guildId: string, stickerId: string, body: { name?: string; description?: string; tags?: string }) => authed<Sticker>(`/guilds/${guildId}/stickers/${stickerId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteSticker: (guildId: string, stickerId: string) => authed<void>(`/guilds/${guildId}/stickers/${stickerId}`, { method: 'DELETE' }),
  webhooks: (guildId: string) => authed<Webhook[]>(`/guilds/${guildId}/webhooks`),
  createWebhook: (channelId: string, name: string) => authed<Webhook>(`/channels/${channelId}/webhooks`, { method: 'POST', body: JSON.stringify({ name }) }),
  updateWebhook: (id: string, patch: { name?: string; channel_id?: string }) => authed<Webhook>(`/webhooks/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  deleteWebhook: (id: string) => authed<void>(`/webhooks/${id}`, { method: 'DELETE' }),
  emojis: (guildId: string) => authed<Emoji[]>(`/guilds/${guildId}/emojis`),
  createEmoji: (guildId: string, body: { name: string; image: string }) => authed<Emoji>(`/guilds/${guildId}/emojis`, { method: 'POST', body: JSON.stringify(body) }),
  renameEmoji: (guildId: string, emojiId: string, name: string) => authed<Emoji>(`/guilds/${guildId}/emojis/${emojiId}`, { method: 'PATCH', body: JSON.stringify({ name }) }),
  deleteEmoji: (guildId: string, emojiId: string) => authed<void>(`/guilds/${guildId}/emojis/${emojiId}`, { method: 'DELETE' }),

  /** Автомод: правила сервера (MANAGE_GUILD). */
  automodRules: (guildId: string) => authed<AutomodRule[]>(`/guilds/${guildId}/auto-moderation/rules`),
  createAutomodRule: (guildId: string, body: AutomodRuleInput) => authed<AutomodRule>(`/guilds/${guildId}/auto-moderation/rules`, { method: 'POST', body: JSON.stringify(body) }),
  updateAutomodRule: (guildId: string, ruleId: string, body: AutomodRuleInput) =>
    authed<AutomodRule>(`/guilds/${guildId}/auto-moderation/rules/${ruleId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteAutomodRule: (guildId: string, ruleId: string) => authed<void>(`/guilds/${guildId}/auto-moderation/rules/${ruleId}`, { method: 'DELETE' }),

  bans: (guildId: string) => authed<Ban[]>(`/guilds/${guildId}/bans`),
  ban: (guildId: string, userId: string, reason?: string) =>
    authed<void>(`/guilds/${guildId}/bans/${userId}`, { method: 'PUT', body: JSON.stringify(reason ? { reason } : {}) }),
  unban: (guildId: string, userId: string) => authed<void>(`/guilds/${guildId}/bans/${userId}`, { method: 'DELETE' }),

  joinVoice: (channelId: string, body: { self_mute?: boolean; self_deaf?: boolean; session_id?: string }) =>
    authed<VoiceJoin>(`/channels/${channelId}/voice/join`, { method: 'POST', body: JSON.stringify(body) }),
  voiceActivity: (channelId: string, sessionId: string) => authed<void>('/users/@me/voice/activity', { method: 'POST', body: JSON.stringify({ channel_id: channelId, session_id: sessionId }) }),
  leaveVoice: () => authed<void>('/users/@me/voice', { method: 'DELETE' }),
  updateVoice: (patch: { self_mute?: boolean; self_deaf?: boolean; self_stream?: boolean; self_video?: boolean }) =>
    authed<VoiceState>('/users/@me/voice', { method: 'PATCH', body: JSON.stringify(patch) }),

  invites: (guildId: string) => authed<Invite[]>(`/guilds/${guildId}/invites`),
  createInvite: (channelId: string, input: { max_age?: number; max_uses?: number; unique?: boolean } = {}) =>
    authed<Invite>(`/channels/${channelId}/invites`, { method: 'POST', body: JSON.stringify(input) }),
  invite: (code: string) => api<Invite>(`/invites/${encodeURIComponent(code)}`),

  settings: (guildId: string) => authed<UserGuildSettings>(`/users/@me/guilds/${guildId}/settings`),
  updateSettings: (guildId: string, patch: SettingsPatch) =>
    authed<UserGuildSettings>(`/users/@me/guilds/${guildId}/settings`, { method: 'PATCH', body: JSON.stringify(patch) }),
  auditLog: (guildId: string, params: { action_type?: number; before?: string; limit?: number } = {}) => {
    const q = new URLSearchParams()
    if (params.action_type) q.set('action_type', String(params.action_type))
    if (params.before) q.set('before', params.before)
    q.set('limit', String(params.limit ?? 50))
    return authed<AuditLog>(`/guilds/${guildId}/audit-logs?${q.toString()}`)
  },
  search: (guildId: string, params: SearchParams) => {
    const q = new URLSearchParams()
    if (params.content) q.set('content', params.content)
    if (params.channel_id) q.set('channel_id', params.channel_id)
    if (params.author_id) q.set('author_id', params.author_id)
    if (params.mentions) q.set('mentions', params.mentions)
    if (params.has) q.set('has', params.has)
    if (params.offset) q.set('offset', String(params.offset))
    return authed<{ total_results: number; messages: Message[][] }>(`/guilds/${guildId}/messages/search?${q.toString()}`)
  },
  messagesAround: (channelId: string, messageId: string, limit = 50) =>
    authed<Message[]>(`/channels/${channelId}/messages?around=${messageId}&limit=${limit}`),
  acceptInvite: (code: string) => authed<Invite>(`/invites/${encodeURIComponent(code)}`, { method: 'POST' }),
  /** Вступление в публичный сервер из каталога, без приглашения. */
  joinDiscovery: (guildId: string) => authed<Guild>(`/discovery/guilds/${guildId}/join`, { method: 'POST' }),
  deleteInvite: (code: string) => authed<Invite>(`/invites/${encodeURIComponent(code)}`, { method: 'DELETE' }),
}

/** Код из ссылки-приглашения или самого кода. */
export function parseInviteCode(raw: string): string | null {
  const s = raw.trim()
  const m = /(?:invite\/|\/i\/)([A-Za-z0-9]{2,32})\/?$/.exec(s)
  if (m?.[1]) return m[1]
  if (/^[A-Za-z0-9]{2,32}$/.test(s)) return s
  return null
}
